import { Modal, OverflowTooltip, Table, useFetch } from "@4uhub/lib4uhub";
import TocIcon from "@mui/icons-material/Toc";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { IExamFilesLog } from "../../../../models/exam-files-log";
import { solvedExam } from "../../../../services/foruBusExamFilesLog.service";
import MainApi from "../../../../services/mainApi.service";
import FileIntegrationModal from "./LogFileIntegrationModal";
import MacroFilter, { IFilterParams } from "./MacroFilter";

const EXAM_FILES_LOG_ROUTE =
  process.env.REACT_APP_4UBUS + "/api/v1/ForuBusExamFilesLog";

const ipmTasyService = new MainApi<IExamFilesLog>(EXAM_FILES_LOG_ROUTE);

const translationPath = "page.4uBus.logs.logFileIntegration.";

const LogFileIntegration = () => {
  const [open, setOpen] = useState(false);

  const [currentLog, setCurrentLog] = useState<IExamFilesLog>();

  const [refresh, setRefresh] = useState(0);

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    Status: "",
    DateStart: new Date(),
    DateEnd: new Date(),
    Solved: "",
  });

  const { sendRequest: solved } = useFetch(solvedExam);

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const columns: GridColDef<IExamFilesLog>[] = [
    {
      field: "robot",
      headerName: t(translationPath + "robot") || "Robot",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Created",
      renderCell: (params) => {
        if (!params.value) return "-";

        const initialDate = new Date(params.value);
        return format(initialDate, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "status",
      headerName: t(translationPath + "status") || "Status",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "message",
      headerName: t(translationPath + "message_status") || "Message status",
      renderCell: (params) => {
        return params.value ? (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            variant="subtitle2"
          />
        ) : (
          "-"
        );
      },
    },
    {
      field: "solved",
      headerName: t(translationPath + "resolved") || "Resolvido",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
  ];

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const closeModalHandler = () => setOpen(false);

  const openModalHandler = (params: GridRowParams<IExamFilesLog>) => {
    setCurrentLog(params.row);
    setOpen(true);
  };

  const onRefreshHandler = () => {
    setRefresh((p) => (p += 1));
  };

  const onResolvedHandler = useCallback(
    async (identifier: number) => {
      const { success } = await solved({
        identifier,
        item: {
          identifier,
          solved: "S",
        },
      });
      if (success) {
        onRefreshHandler();
      }
    },
    [solved, onRefreshHandler],
  );

  return (
    <>
      <Modal
        open={open}
        onClose={closeModalHandler}
        title={`${t(translationPath + "logs")}`}
      >
        {currentLog && <FileIntegrationModal items={currentLog.items} />}
      </Modal>
      <Table<IExamFilesLog>
        title={t("components.menu.4ubus.logs.examFilesLogIntegration")}
        columns={columns}
        service={ipmTasyService}
        onRowClick={() => {}}
        showButton={false}
        onFetchData={() => {}}
        showDeleteButton={false}
        showDefaultMacroFilters={false}
        searchInputPlaceHolder={""}
        getRowId={(r) => r.examFilesLogIdentitifer}
        serviceProps={{
          refresh,
          ...filterParams,
          DateStart: filterParams?.DateStart
            ? new Date(
                filterParams.DateStart.setUTCHours(0, 0, 0, 0),
              ).toISOString()
            : undefined,
          DateEnd: filterParams?.DateEnd
            ? new Date(
                filterParams.DateEnd.setUTCHours(23, 59, 59, 999),
              ).toISOString()
            : undefined,
        }}
        rowActions={(param) => {
          return [
            param.row.items && param.row.items.length > 0
              ? {
                  text: t(translationPath + "view_logs"),
                  action: openModalHandler,
                  icon: <TocIcon />,
                }
              : null,
            param.row.solved === "N"
              ? {
                  text: t(translationPath + "solved"),
                  action: (params) => {
                    onResolvedHandler(params.row.examFilesLogIdentitifer);
                  },
                  icon: <CheckCircleIcon />,
                }
              : null,
          ];
        }}
        macroFilters={() => (
          <MacroFilter
            filterParams={filterParams}
            onFilterParams={handleFilterChange}
          />
        )}
      />
    </>
  );
};

export default LogFileIntegration;
